import { Model, type ModelProps } from '@/infra/model'
import { collection } from '@/infra/collection'
import { Id } from '@/infra/id'
import { action, computed, observable } from 'mobx'
import { once } from 'lodash-es'
import { unixEpoch } from '@/utils/date-util'
import { User } from '@/features/iam/users/User'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { BundleBaseItem, type BundleBaseItemProps } from './BundleBaseItem'
import {
  BundleIncludedItem,
  type BundleIncludedItemProps,
} from './BundleIncludedItem'

export interface BundleProps extends ModelProps<Bundle> {
  /**
   * Included items, base item first.
   */
  includedItems?: BundleIncludedItemProps[]
  /**
   * The base item; `null` when the bundle has none.
   */
  baseItem?: BundleBaseItemProps | null
}

const BundleLazyNone = once(() => new Bundle())

export class Bundle extends Model<Bundle> {
  @observable.ref private accessor _baseItem: BundleBaseItem =
    BundleBaseItem.none

  id = Id.none

  @observable accessor workspace: Workspace = Workspace.none
  @observable accessor archived = false
  @observable accessor name = ''
  @observable accessor description = ''
  @observable accessor price = 0
  @observable accessor createdBy = User.none
  @observable accessor dateCreated = unixEpoch()
  @observable accessor archivedBy: User | null = null
  @observable accessor dateArchived: Date | null = null
  @observable accessor lastEditedBy: User | null = null
  @observable accessor dateLastEdited: Date | null = null
  @observable accessor archivedItemsCount = 0

  /**
   * Included items, base item first.
   */
  readonly includedItems = collection({
    create: () => new BundleIncludedItem(),
    getDataId: (d) => d.inventoryItemId,
    getModelId: (m) => m.inventoryItemId,
  })

  get baseItem() {
    return this._baseItem
  }

  @computed
  get containsArchivedItems() {
    return this.archivedItemsCount > 0
  }

  static get none() {
    return BundleLazyNone()
  }

  @action.bound
  override set({ includedItems, baseItem, ...props }: Partial<BundleProps>) {
    this.includedItems.reset(includedItems)

    if (baseItem === null) {
      this._baseItem = BundleBaseItem.none
    } else if (baseItem) {
      if (this._baseItem === BundleBaseItem.none) {
        this._baseItem = new BundleBaseItem().set(baseItem)
      } else {
        this._baseItem.set(baseItem)
      }
    }

    return super.set(props)
  }
}
